import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal, Tree, Table, Button, Space, Empty, message } from 'antd';
import { SearchOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { collectDefaultExpandedOrgTreeKeys } from '../../utils/orgTree';
import './OrgTreeEmployeePicker.css';

function formatSelectedLabel(employee) {
  if (!employee) return '';
  return `${employee.real_name}（${employee.employee_no}）`;
}

export default function OrgTreeEmployeePicker({
  value,
  onChange,
  onEmployeeSelect,
  processType = 'resignation',
  placeholder = '请从组织架构选择员工',
  disabled = false
}) {
  const [open, setOpen] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedOrgKeys, setSelectedOrgKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const fetchSeqRef = useRef(0);

  const loadTree = useCallback(async () => {
    try {
      setTreeLoading(true);
      const res = await api.get('/hr/processes/employee-picker/org-tree', {
        params: { process_type: processType }
      });
      if (res.data.success) {
        const tree = res.data.data || [];
        setTreeData(tree);
        setExpandedKeys(collectDefaultExpandedOrgTreeKeys(tree));
      }
    } catch {
      message.error('加载组织架构失败');
    } finally {
      setTreeLoading(false);
    }
  }, [processType]);

  const loadEmployees = useCallback(async (orgCode, searchKw = '') => {
    const requestId = ++fetchSeqRef.current;
    try {
      setEmpLoading(true);
      const res = await api.get('/hr/processes/employee-picker/employees', {
        params: {
          process_type: processType,
          org_id: orgCode || undefined,
          keyword: searchKw || undefined
        }
      });
      if (requestId !== fetchSeqRef.current) return;
      if (res.data.success) setEmployees(res.data.data || []);
    } catch {
      if (requestId !== fetchSeqRef.current) return;
      setEmployees([]);
    } finally {
      if (requestId === fetchSeqRef.current) setEmpLoading(false);
    }
  }, [processType]);

  const loadSelectedSummary = useCallback(async (employeeNo) => {
    if (!employeeNo) {
      setSelectedEmployee(null);
      return;
    }
    try {
      const res = await api.get(`/hr/processes/employee-picker/employee/${employeeNo}`, {
        params: { process_type: processType }
      });
      if (res.data.success) setSelectedEmployee(res.data.data);
    } catch {
      setSelectedEmployee(null);
    }
  }, [processType]);

  useEffect(() => {
    loadSelectedSummary(value);
  }, [value, loadSelectedSummary]);

  useEffect(() => {
    if (!open) return;
    loadTree();
    loadEmployees(null, '');
    setKeyword('');
    setPendingEmployee(null);
    if (value) setPendingEmployee({ employee_no: value, id: value });
  }, [open, loadTree, loadEmployees, value]);

  useEffect(() => {
    if (!open) return;
    loadEmployees(selectedOrg?.org_code || null, keyword);
  }, [open, selectedOrg, keyword, loadEmployees]);

  const displayValue = useMemo(
    () => formatSelectedLabel(selectedEmployee),
    [selectedEmployee]
  );

  const handleOpen = () => {
    if (!disabled) setOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingEmployee?.employee_no) {
      message.warning('请选择员工');
      return;
    }
    setSelectedEmployee(pendingEmployee);
    const employeeNo = pendingEmployee.employee_no;
    onChange?.(employeeNo);
    onEmployeeSelect?.(employeeNo, pendingEmployee);
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.stopPropagation?.();
    setSelectedEmployee(null);
    onChange?.(undefined);
    onEmployeeSelect?.(undefined, null);
  };

  const columns = [
    { title: '姓名', dataIndex: 'real_name', width: 100 },
    { title: '工号', dataIndex: 'employee_no', width: 120 },
    { title: '部门/项目', dataIndex: 'dept_name', ellipsis: true },
    { title: '岗位', dataIndex: 'position_title_display', ellipsis: true },
    { title: '手机', dataIndex: 'mobile', width: 120 }
  ];

  return (
    <>
      <Input
        readOnly
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        suffix={(
          <Space size={4}>
            {value && !disabled && (
              <Button type="link" size="small" onClick={handleClear} style={{ padding: 0 }}>
                清除
              </Button>
            )}
            <Button
              type="link"
              size="small"
              icon={<SearchOutlined />}
              onClick={(e) => { e.stopPropagation(); handleOpen(); }}
              disabled={disabled}
              style={{ padding: 0 }}
            >
              选择
            </Button>
          </Space>
        )}
        onClick={handleOpen}
      />

      <Modal
        title="从组织架构选择员工"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleConfirm}
        width={920}
        destroyOnClose
        okText="确定"
        cancelText="取消"
      >
        <div className="org-tree-employee-picker">
          <div className="org-tree-employee-picker-tree">
            <div className="org-tree-employee-picker-panel-title">
              <TeamOutlined /> 组织架构
            </div>
            {treeLoading ? (
              <Empty description="加载中..." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : treeData.length ? (
              <Tree
                treeData={treeData}
                selectedKeys={selectedOrgKeys}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                onSelect={(keys, info) => {
                  if (!keys.length) {
                    setSelectedOrg(null);
                    setSelectedOrgKeys([]);
                    return;
                  }
                  setSelectedOrgKeys(keys);
                  setSelectedOrg(info.node);
                }}
                showLine={{ showLeafIcon: false }}
              />
            ) : (
              <Empty description="暂无组织数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>

          <div className="org-tree-employee-picker-list">
            <div className="org-tree-employee-picker-panel-title">
              <UserOutlined />
              {' '}
              {selectedOrg?.title || selectedOrg?.org_name || '全部可见员工'}
            </div>
            <Input.Search
              allowClear
              placeholder="搜索工号/姓名/手机"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(v) => setKeyword(v)}
              style={{ marginBottom: 12 }}
            />
            <Table
              size="small"
              rowKey="employee_no"
              columns={columns}
              dataSource={employees}
              loading={empLoading}
              pagination={false}
              scroll={{ y: 360 }}
              locale={{ emptyText: '暂无在职员工' }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: pendingEmployee?.employee_no ? [pendingEmployee.employee_no] : [],
                onChange: (_, rows) => setPendingEmployee(rows[0] || null)
              }}
              onRow={(record) => ({
                onClick: () => setPendingEmployee(record)
              })}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
